import { Box, Group, Text, UnstyledButton } from '@mantine/core';
import styled from 'styled-components';
import {
	IconArrowsDiagonal,
	IconArrowsDiagonalMinimize,
	IconSearch,
	IconTarget,
} from '@tabler/icons-react';
import type { ReactNode } from 'react';
import type { CSSObject } from 'styled-components';
import type { StyledMantineProps } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/styledMantine.types';
import { defaultTheme } from '@dealstack/frontend/shared/mechanism/styleguide/defaultTheme';
import { useTranslationCapManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ToolbarSlotCell, dsAiToolCellStyles } from './DataGrid.styles';
import { dsAiFocusWithinRingStyles } from '@dealstack/frontend/app/pages/Project/Cap/DealstackAi/theme/dealstackAi.tokens';

interface Props {
	searchPlaceholder: string;
	search: string;
	onSearchChange: (value: string) => void;
	/** The quick-filter select cell ("All (500)"); unused when `toolCells` is set. */
	quickFilter?: ReactNode;
	/** The supported Filter control; unused when `toolCells` is set. */
	filterCell?: ReactNode;
	/** The column visibility control; unused when `toolCells` is set. */
	columnsCell?: ReactNode;
	/** Replaces the Bulk actions cell; `null` drops it. */
	leadingCell?: ReactNode;
	searchIcon?: ReactNode;
	/** Replaces the Filter | Columns | quick filter | expand cells. */
	toolCells?: ReactNode;
	lightDivider?: boolean;
	/** Filter and Columns show their icons only in a narrow table (design `.tb-l`). */
	compactLabels?: boolean;
	expanded?: boolean;
	onExpand?: () => void;
}

/**
 * Table action bar: Bulk actions | Table search | Filter | Columns | quick filter | expand.
 * Callers provide the supported actions; the default Bulk actions cell is visibly disabled with an explanation.
 */
export const TableToolbar = ({
	searchPlaceholder,
	search,
	onSearchChange,
	quickFilter,
	filterCell,
	columnsCell,
	leadingCell = <BulkActionsCell />,
	searchIcon = <IconSearch size={24} stroke={1.25} />,
	expanded = false,
	onExpand,
	toolCells = (
		<WorkspaceToolCells
			quickFilter={quickFilter}
			filterCell={filterCell}
			columnsCell={columnsCell}
			expanded={expanded}
			onExpand={onExpand}
		/>
	),
	lightDivider = false,
	compactLabels = false,
}: Props) => (
	<Group
		align="stretch"
		gap={0}
		wrap="nowrap"
		h="48px"
		bg="gray.0"
		c="dark.8"
		fz="md"
		style={{
			borderBottom: lightDivider
				? '1px solid var(--mantine-color-gray-2)'
				: '1px solid var(--mantine-color-gray-4)',
		}}
		data-compact-labels={compactLabels || undefined}
	>
		{leadingCell}
		<Group
			component="label"
			align="center"
			gap={8}
			wrap="nowrap"
			flex={1}
			miw={120}
			px={12}
			styles={{ root: SEARCH_CELL_STYLES }}
		>
			{searchIcon}
			<SearchInput
				component="input"
				type="search"
				value={search}
				placeholder={searchPlaceholder}
				aria-label={searchPlaceholder}
				onChange={(event) => onSearchChange(event.currentTarget.value)}
				flex={1}
				miw={0}
				c="dark.8"
			/>
		</Group>
		{toolCells}
	</Group>
);

const BulkActionsCell = () => {
	const tCapManage = useTranslationCapManage();
	return (
		<UnstyledButton
			type="button"
			styles={{ root: dsAiToolCellStyles(160) }}
			disabled
			title={tCapManage('dealstack_ai.table.bulk_unavailable')}
		>
			<IconTarget size={24} stroke={1.25} />
			<Text span size="md" fw={600}>
				{tCapManage('dealstack_ai.table.bulk_actions')}
			</Text>
		</UnstyledButton>
	);
};

const WorkspaceToolCells = ({
	quickFilter,
	filterCell,
	columnsCell,
	expanded,
	onExpand,
}: Pick<Props, 'quickFilter' | 'filterCell' | 'columnsCell' | 'expanded' | 'onExpand'>) => {
	const tCapManage = useTranslationCapManage();
	const expandLabel = tCapManage(
		expanded ? 'dealstack_ai.table.restore' : 'dealstack_ai.table.expand'
	);
	return (
		<>
			<ToolbarSlotCell $compact>{filterCell}</ToolbarSlotCell>
			<ToolbarSlotCell $compact>{columnsCell}</ToolbarSlotCell>
			<ToolbarSlotCell>{quickFilter}</ToolbarSlotCell>
			<UnstyledButton
				type="button"
				styles={{ root: dsAiToolCellStyles(48) }}
				aria-label={expandLabel}
				title={expandLabel}
				disabled={!onExpand}
				onClick={onExpand}
			>
				{expanded ? (
					<IconArrowsDiagonalMinimize size={20} stroke={1.25} />
				) : (
					<IconArrowsDiagonal size={20} stroke={1.25} />
				)}
			</UnstyledButton>
		</>
	);
};

/** A divider only where a cell precedes the search: `* + &` is the sibling form of a `:first-child` reset. */
const SEARCH_CELL_STYLES = {
	'* + &': { borderLeft: `1px solid ${defaultTheme.v3.colors.gray[2]}` },
	...dsAiFocusWithinRingStyles(-2),
} satisfies CSSObject;

const SEARCH_INPUT_STYLES = {
	border: 'none',
	background: 'transparent',
	font: 'inherit',
	outline: 'none',
	'&::placeholder': { color: defaultTheme.v3.colors.dark[8] },
} satisfies CSSObject;

/** The toolbar's search field. */
const SearchInput = styled(Box)<StyledMantineProps<'input'>>(SEARCH_INPUT_STYLES);
